import React, { useRef, useEffect, useState, useCallback } from 'react'
import * as faceapi from 'face-api.js'
import './WebcamFeed.css'

const WebcamFeed = ({ isActive, onToggle, onEmotionDetected, modelsLoaded }) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const animationRef = useRef(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [error, setError] = useState(null)
  const [faceDetected, setFaceDetected] = useState(false)

  const startWebcam = useCallback(async () => {
    try {
      setIsInitializing(true)
      setError(null)
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      })
      
      streamRef.current = stream
      videoRef.current.srcObject = stream
      
      await new Promise((resolve) => {
        videoRef.current.onloadedmetadata = resolve
      })
      
      onToggle(true)
      setIsInitializing(false)
    } catch (err) {
      console.error('Error accessing webcam:', err)
      setError(
        err.name === 'NotAllowedError'
          ? 'Camera access denied. Please allow camera permissions to use emotion detection.'
          : 'Failed to access webcam. Please check your camera connection.'
      )
      setIsInitializing(false)
    }
  }, [onToggle])

  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    onToggle(false)
    setFaceDetected(false)
    setError(null)
  }, [onToggle])

  const detectEmotions = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !modelsLoaded) return

    try {
      const detections = await faceapi
        .detectAllFaces(videoRef.current)
        .withFaceLandmarks()
        .withFaceExpressions()

      const canvas = canvasRef.current
      const context = canvas.getContext('2d')
      
      // Clear previous drawings
      context.clearRect(0, 0, canvas.width, canvas.height)

      if (detections.length > 0) {
        setFaceDetected(true)
        
        // Draw face detection results
        const resizedDetections = faceapi.resizeResults(detections, {
          width: canvas.width,
          height: canvas.height
        })

        // Draw face rectangles
        resizedDetections.forEach(detection => {
          const box = detection.detection.box
          const drawBox = new faceapi.draw.DrawBox(box, {
            label: 'Face Detected',
            lineWidth: 2,
            boxColor: '#00ff88'
          })
          drawBox.draw(canvas)
        })

        // Get the first detected face's emotions
        const emotions = detections[0].expressions
        const emotionEntries = Object.entries(emotions)
        const dominantEmotion = emotionEntries.reduce((a, b) => 
          emotions[a[0]] > emotions[b[0]] ? a : b
        )

        const emotionData = {
          emotion: dominantEmotion[0],
          confidence: Math.round(dominantEmotion[1] * 100),
          allEmotions: emotions
        }

        onEmotionDetected(emotionData)
      } else {
        setFaceDetected(false)
        onEmotionDetected(null)
      }
    } catch (err) {
      console.error('Error detecting emotions:', err)
    }

    // Continue detection loop
    if (isActive) {
      animationRef.current = requestAnimationFrame(detectEmotions)
    }
  }, [isActive, modelsLoaded, onEmotionDetected])

  useEffect(() => {
    if (isActive && modelsLoaded) {
      detectEmotions()
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isActive, modelsLoaded, detectEmotions])

  useEffect(() => {
    return () => {
      stopWebcam()
    }
  }, [stopWebcam])

  const handleToggleWebcam = () => {
    if (isActive) {
      stopWebcam()
    } else {
      startWebcam()
    }
  }

  return (
    <div className="webcam-container">
      <div className="webcam-header">
        <h3 className="webcam-title">Live Camera Feed</h3>
        <div className="webcam-status">
          {faceDetected && (
            <span className="status-indicator face-detected">
              <span className="status-dot"></span>
              Face Detected
            </span>
          )}
          {isActive && !faceDetected && (
            <span className="status-indicator no-face">
              <span className="status-dot"></span>
              No Face Detected
            </span>
          )}
        </div>
      </div>

      <div className="video-container">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className={`webcam-video ${isActive ? 'active' : ''}`}
          style={{ display: isActive ? 'block' : 'none' }}
        />
        
        <canvas
          ref={canvasRef}
          className={`detection-canvas ${isActive ? 'active' : ''}`}
          style={{ display: isActive ? 'block' : 'none' }}
        />

        {!isActive && (
          <div className="webcam-placeholder">
            <div className="placeholder-icon">📷</div>
            <h4>Camera Feed</h4>
            <p>Click the button below to start emotion detection</p>
          </div>
        )}

        {isInitializing && (
          <div className="webcam-loading">
            <div className="loading-spinner"></div>
            <p>Initializing camera...</p>
          </div>
        )}

        {error && (
          <div className="webcam-error">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
          </div>
        )}
      </div>

      <div className="webcam-controls">
        <button
          onClick={handleToggleWebcam}
          disabled={isInitializing || !modelsLoaded}
          className={`webcam-toggle ${isActive ? 'active' : ''}`}
          aria-label={isActive ? 'Stop camera' : 'Start camera'}
        >
          {isInitializing ? (
            <>
              <span className="button-icon">⏳</span>
              Initializing...
            </>
          ) : isActive ? (
            <>
              <span className="button-icon">⏹️</span>
              Stop Camera
            </>
          ) : (
            <>
              <span className="button-icon">▶️</span>
              Start Camera
            </>
          )}
        </button>

        {!modelsLoaded && (
          <p className="models-loading">
            Loading AI models... Please wait.
          </p>
        )}
      </div>

      <div className="webcam-info">
        <div className="info-item">
          <span className="info-icon">🔒</span>
          <span className="info-text">All processing happens locally on your device</span>
        </div>
        <div className="info-item">
          <span className="info-icon">⚡</span>
          <span className="info-text">Real-time emotion detection with face-api.js</span>
        </div>
      </div>
    </div>
  )
}

export default WebcamFeed 